import type { NavItem } from './nav-items';

export type NavigationAliases = Record<string, string[] | undefined>;

function normalizePath(path: string): string {
  if (path === '/') return path;
  return path.replace(/\/+$/, '');
}

export function isPathWithin(
  pathname: string,
  candidatePath: string,
  includeDescendants = true,
): boolean {
  const current = normalizePath(pathname);
  const candidate = normalizePath(candidatePath);

  if (current === candidate) return true;
  return includeDescendants && candidate !== '/' && current.startsWith(`${candidate}/`);
}

function getMatchScore(pathname: string, item: NavItem, aliases: NavigationAliases): number {
  const current = normalizePath(pathname);
  const href = normalizePath(item.href);

  if (current === href) return 30_000 + href.length;

  for (const path of aliases[item.href] ?? []) {
    const normalizedAlias = normalizePath(path);
    if (current === normalizedAlias) return 30_000 + normalizedAlias.length;
    if (isPathWithin(current, normalizedAlias)) return 20_000 + normalizedAlias.length;
  }

  for (const path of item.activePaths ?? []) {
    const normalizedActivePath = normalizePath(path);
    if (current === normalizedActivePath) return 25_000 + normalizedActivePath.length;
    if (isPathWithin(current, normalizedActivePath)) {
      return 20_000 + normalizedActivePath.length;
    }
  }

  if (isPathWithin(current, href)) return 10_000 + href.length;
  return -1;
}

export function getActiveChildHref(
  pathname: string,
  children: NavItem[] | undefined,
  aliases: NavigationAliases = {},
): string | null {
  let activeHref: string | null = null;
  let bestScore = -1;

  for (const child of children ?? []) {
    const score = getMatchScore(pathname, child, aliases);
    if (score > bestScore) {
      bestScore = score;
      activeHref = child.href;
    }
  }

  return activeHref;
}
